import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as SortIcon } from '@assets/images/Icons/Sort.svg'
import { ReactComponent as TweakIcon } from '@assets/images/Icons/Tweak.svg'
import { ReactComponent as CheckmarkMenuIcon } from '@assets/images/Icons/Checkmark_menu.svg'
import Checkbox from '../Checkbox/Checkbox.component'
import type { SortFilterProps } from './SortFilter.types'
import './SortFilter.css'

type MenuPosition = {
  top: number
  left: number
}

const MENU_GAP = 8

const SortFilter: React.FC<SortFilterProps> = (props) => {
  const {
    mode,
    darkMode = false,
    open,
    defaultOpen = false,
    onOpenChange,
    previewState,
    disabled = false,
    className = '',
    ariaLabel,
  } = props

  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const [position, setPosition] = useState<MenuPosition | null>(null)

  const isPreview = previewState != null
  const isControlled = open !== undefined
  const isOpen = isPreview
    ? previewState === 'open'
    : isControlled
      ? open
      : internalOpen

  const sortValues = props.mode === 'sort' ? props.values : null
  const filterSelectedIds = props.mode === 'filter' ? props.selectedIds : null

  const setOpen = useCallback(
    (nextOpen: boolean): void => {
      if (!isControlled) {
        setInternalOpen(nextOpen)
      }
      onOpenChange?.(nextOpen)
    },
    [isControlled, onOpenChange],
  )

  const updatePosition = useCallback((): void => {
    const trigger = triggerRef.current
    const menu = menuRef.current
    if (!trigger || !menu) {
      return
    }

    const triggerRect = trigger.getBoundingClientRect()
    const menuRect = menu.getBoundingClientRect()

    let left = triggerRect.left - 1
    if (left + menuRect.width > window.innerWidth - MENU_GAP) {
      left = triggerRect.right - menuRect.width
    }
    if (left < MENU_GAP) {
      left = MENU_GAP
    }

    let top = triggerRect.bottom + MENU_GAP
    if (top + menuRect.height > window.innerHeight - MENU_GAP) {
      top = triggerRect.top - MENU_GAP - menuRect.height
    }

    setPosition({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (isPreview || !isOpen) {
      setPosition(null)
      return
    }

    updatePosition()
  }, [isOpen, isPreview, updatePosition, mode, sortValues, filterSelectedIds])

  useEffect(() => {
    if (isPreview || !isOpen) {
      return undefined
    }

    const handleReposition = (): void => updatePosition()

    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [isOpen, isPreview, updatePosition])

  useEffect(() => {
    if (isPreview || !isOpen) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (
        !rootRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [isOpen, isPreview, setOpen])

  useEffect(() => {
    if (isPreview || !isOpen) {
      return undefined
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, isPreview, setOpen])

  const classNames = [
    'ds-sort-filter',
    isOpen ? 'ds-sort-filter--open' : '',
    darkMode ? 'ds-sort-filter--dark' : '',
    isPreview ? 'ds-sort-filter--preview' : '',
    previewState ? `ds-sort-filter--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const menuClassNames = [
    'ds-sort-filter__menu',
    darkMode ? 'ds-sort-filter__menu--dark' : '',
    !isPreview && isOpen ? 'ds-sort-filter__menu--portal' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const defaultAriaLabel = mode === 'sort' ? 'Sort' : 'Filter'

  const renderSortMenu = (): React.ReactNode => {
    if (props.mode !== 'sort') {
      return null
    }

    const { sections, values, onChange } = props

    return sections.map((section) => (
      <React.Fragment key={section.id}>
        <div className="ds-sort-filter__section-title">
          <p className="ds-sort-filter__section-title-text">{section.title}</p>
        </div>
        {section.options.map((option) => {
          const selected = values[section.id] === option.id
          return (
            <button
              key={option.id}
              type="button"
              role="menuitemradio"
              aria-checked={selected}
              className={[
                'ds-sort-filter__option',
                selected ? 'ds-sort-filter__option--selected' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              tabIndex={isPreview ? -1 : undefined}
              onClick={() => {
                if (isPreview) {
                  return
                }
                onChange?.(section.id, option.id)
              }}
            >
              <span className="ds-sort-filter__option-inner">
                <span className="ds-sort-filter__check" aria-hidden="true">
                  <CheckmarkMenuIcon />
                </span>
                <span className="ds-sort-filter__option-label">
                  {option.label}
                </span>
              </span>
            </button>
          )
        })}
      </React.Fragment>
    ))
  }

  const renderFilterMenu = (): React.ReactNode => {
    if (props.mode !== 'filter') {
      return null
    }

    const { title = 'Filter', options, selectedIds, onChange } = props

    return (
      <>
        <div className="ds-sort-filter__section-title">
          <p className="ds-sort-filter__section-title-text">{title}</p>
        </div>
        {options.map((option) => {
          const checked = selectedIds.includes(option.id)
          return (
            <div
              key={option.id}
              className="ds-sort-filter__filter-option"
              role="none"
            >
              <Checkbox
                label={option.label}
                checked={checked}
                darkMode={darkMode}
                tabIndex={isPreview ? -1 : undefined}
                onChange={() => {
                  if (isPreview) {
                    return
                  }
                  const next = checked
                    ? selectedIds.filter((id) => id !== option.id)
                    : [...selectedIds, option.id]
                  onChange?.(next)
                }}
              />
            </div>
          )
        })}
      </>
    )
  }

  const menuContent = mode === 'sort' ? renderSortMenu() : renderFilterMenu()

  return (
    <div ref={rootRef} className={classNames}>
      <button
        ref={triggerRef}
        type="button"
        className="ds-sort-filter__trigger"
        aria-label={ariaLabel ?? defaultAriaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={disabled}
        tabIndex={isPreview ? -1 : undefined}
        onClick={() => {
          if (isPreview || disabled) {
            return
          }
          setOpen(!isOpen)
        }}
      >
        {mode === 'sort' ? (
          <SortIcon className="ds-sort-filter__icon" aria-hidden="true" />
        ) : (
          <TweakIcon
            className="ds-sort-filter__icon ds-sort-filter__icon--tweak"
            aria-hidden="true"
          />
        )}
      </button>

      {isOpen && isPreview ? (
        <div
          className={menuClassNames}
          role="menu"
          aria-label={defaultAriaLabel}
        >
          {menuContent}
        </div>
      ) : null}

      {isOpen && !isPreview
        ? createPortal(
            <div
              ref={menuRef}
              className={menuClassNames}
              role="menu"
              aria-label={ariaLabel ?? defaultAriaLabel}
              style={{
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                visibility: position ? 'visible' : 'hidden',
              }}
            >
              {menuContent}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

export default SortFilter
